'use client'

import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CameraIcon, FilesIcon, X } from 'lucide-react'
import Zoom from 'react-medium-image-zoom'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  toast,
} from '@ui/web/components'
import {
  FileUpload,
  FileUploadDropzone,
  FileUploadItem,
  FileUploadItemDelete,
  FileUploadItemMetadata,
  FileUploadItemPreview,
  FileUploadList,
  FileUploadTrigger,
} from '@ui/web/lib'
import { buildUrl } from '@helper/utils/format'
import { useDetectDevice } from '@helper/web/hooks'

import { useSharedTRPC } from '../../context'
import { AreYouSureDialog } from '../are-you-sure-dialog'
import { CameraModal } from './camera-modal'

// ---------------------------------------------------------------------------
// Base — presentational, no tRPC, safe for Storybook
// ---------------------------------------------------------------------------

export interface InClassUploadWorkModalBaseProps {
  open: boolean
  onOpenChange: (open: boolean) => void | React.Dispatch<React.SetStateAction<boolean>>
  files: File[]
  onFilesChange: (files: File[]) => void
  onUpload: () => void
  loading?: boolean
  errorMsg?: string | null
  childSubmissions?: { id: string; fileUrl: string | null }[]
}

export function InClassUploadWorkModalBase({
  open,
  onOpenChange,
  files,
  onFilesChange,
  onUpload,
  loading = false,
  errorMsg = null,
  childSubmissions = [],
}: InClassUploadWorkModalBaseProps) {
  const device = useDetectDevice()

  const [isCameraOpen, setIsCameraOpen] = React.useState(false)
  const [fileErrorMsg, setFileErrorMsg] = React.useState<string | null>(null)
  const [openAreYouSureDialog, setOpenAreYouSureDialog] = React.useState(false)

  const existingImages = childSubmissions.filter((s) => s.fileUrl)
  const MAX_FILES = 5
  const remainingSlots = Math.max(0, MAX_FILES - existingImages.length)

  return (
    <>
      <AlertDialog
        open={open}
        onOpenChange={(open) => {
          if (loading) return

          if (!open) {
            onFilesChange([])
            setFileErrorMsg(null)
            setIsCameraOpen(false)
            setOpenAreYouSureDialog(false)
          }

          onOpenChange(open)
        }}
      >
        <AlertDialogContent className='srd:data-[size=default]:max-w-lg srd:w-full'>
          <AlertDialogHeader className='srd:flex srd:flex-col srd:gap-y-2 srd:px-6 srd:pt-6'>
            <AlertDialogTitle className='srd:text-left srd:w-full'>Hasil Lembar Kerjaku</AlertDialogTitle>
            <AlertDialogDescription className='srd:text-left srd:w-full'>
              Unggah hasil kerjamu dengan format jpg, jpeg, atau png.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className='srd:w-full srd:flex srd:flex-col srd:gap-y-4 srd:px-6 srd:pb-6'>
            {existingImages.length > 0 && (
              <div className='srd:flex srd:flex-col srd:gap-y-2'>
                <p className='srd:text-neutral-black srd:font-medium srd:text-left'>Unggahan sebelumnya</p>
                <div className='srd:flex srd:items-center srd:w-full srd:flex-wrap srd:gap-2'>
                  {existingImages.map((submission, index) => (
                    <div
                      key={submission.id}
                      className='srd:flex srd:size-20 srd:shrink-0 srd:items-center srd:justify-center srd:overflow-hidden srd:rounded srd:border srd:border-navy-20 relative'
                    >
                      <Zoom>
                        <img
                          alt={`submission-${index}`}
                          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                          src={submission.fileUrl!}
                          className='srd:size-full srd:object-cover'
                          decoding='async'
                          loading='lazy'
                        />
                      </Zoom>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <FileUpload
              value={files}
              onValueChange={onFilesChange}
              maxFiles={remainingSlots}
              maxSize={5 * 1024 * 1024}
              className='srd:w-full srd:max-w-md'
              onFileReject={(file, message) =>
                setFileErrorMsg(
                  `"${file.name.length > 20 ? `${file.name.slice(0, 20)}...` : file.name}" ditolak. ${message}`,
                )
              }
              multiple
              accept='image/*'
            >
              <FileUploadDropzone className='srd:border-main-blue-50 srd:flex srd:flex-col srd:gap-y-6'>
                <div className='srd:flex srd:flex-col srd:items-center srd:gap-4 srd:text-center'>
                  <p className='srd:text-sm srd:text-main-blue-100'>Drag & drop file atau</p>

                  <div className='srd:flex srd:items-center srd:justify-center srd:flex-wrap srd:gap-1'>
                    <FileUploadTrigger asChild>
                      <Button type='button' className='srd:rounded-full srd:bg-main-blue-20 srd:text-main-blue-100'>
                        <FilesIcon className='srd:w-4 srd:h-4 srd:mr-2 srd:text-main-blue-100' />
                        {device.isDesktop ? 'Pilih file' : 'Pilih file / Ambil foto'}
                      </Button>
                    </FileUploadTrigger>

                    {device.isDesktop && (
                      <Button
                        type='button'
                        className='srd:rounded-full srd:bg-main-blue-20 srd:text-main-blue-100'
                        onClick={(e) => {
                          e.stopPropagation()
                          setIsCameraOpen(true)
                        }}
                      >
                        <CameraIcon className='srd:w-4 srd:h-4 srd:mr-2 srd:text-main-blue-100' />
                        Ambil foto
                      </Button>
                    )}
                  </div>

                  <p className='srd:text-neutrals-n-50 srd:text-xs'>
                    {remainingSlots === 0
                      ? 'Batas unggahan sudah tercapai (5 file).'
                      : `Sisa ${remainingSlots} dari ${MAX_FILES} file, maksimal 5MB per file.`}
                  </p>
                </div>
              </FileUploadDropzone>
              <FileUploadList orientation='horizontal'>
                {files.map((file, index) => (
                  <FileUploadItem key={index} value={file} className='srd:p-0'>
                    <FileUploadItemPreview className='srd:size-20' />

                    <FileUploadItemMetadata className='srd:sr-only' />

                    <FileUploadItemDelete asChild>
                      <Button
                        variant='secondary'
                        size='icon'
                        className='srd:-top-1 srd:-right-1 srd:absolute srd:size-5 srd:rounded-full'
                      >
                        <X className='srd:size-3' />
                      </Button>
                    </FileUploadItemDelete>
                  </FileUploadItem>
                ))}
              </FileUploadList>
            </FileUpload>

            {fileErrorMsg && <p className='srd:text-main-red-100 srd:text-sm srd:text-left'>{fileErrorMsg}</p>}
            {errorMsg && <p className='srd:text-main-red-100 srd:text-sm srd:text-left'>{errorMsg}</p>}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={loading}
              onClick={() => {
                if (files.length > 0) {
                  setOpenAreYouSureDialog(true)
                } else {
                  onOpenChange(false)
                }
              }}
            >
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                onUpload()
              }}
              disabled={loading || files.length === 0 || remainingSlots === 0}
              loading={loading}
            >
              Unggah hasil kerja
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CameraModal
        open={isCameraOpen}
        onOpenChange={setIsCameraOpen}
        onUpload={(file) => {
          onFilesChange([...files, file])
          setIsCameraOpen(false)
        }}
      />

      <AreYouSureDialog
        open={openAreYouSureDialog}
        onOpenChange={setOpenAreYouSureDialog}
        onConfirm={() => {
          onFilesChange([])
          setOpenAreYouSureDialog(false)
          onOpenChange(false)
        }}
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Smart wrapper — adds tRPC upload logic
// ---------------------------------------------------------------------------

export interface InClassUploadWorkModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void | React.Dispatch<React.SetStateAction<boolean>>
  learnProgressId: string
  onSuccess?: () => void
}

export function InClassUploadWorkModal({
  open,
  onOpenChange,
  learnProgressId,
  onSuccess,
}: InClassUploadWorkModalProps) {
  const trpc = useSharedTRPC()
  const queryClient = useQueryClient()

  const [files, setFiles] = React.useState<File[]>([])
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null)

  const { data: childSubmissions = [] } = useQuery(
    trpc.lesson.getMyLearnProgressAttachments.queryOptions({ learnProgressId }),
  )

  function reset() {
    setFiles([])
    setErrorMsg(null)
  }

  const addAttachmentMutation = useMutation(
    trpc.lesson.addInClassAttachment.mutationOptions({
      onSuccess: async () => {
        toast.success('Hasil kerja berhasil diunggah')
        await queryClient.invalidateQueries({
          queryKey: trpc.lesson.getMyLearnProgressAttachments.queryKey({ learnProgressId }),
        })
        onSuccess?.()
        reset()
        onOpenChange(false)
      },
      onError: () => {
        toast.error('Gagal mengunggah hasil kerja')
      },
    }),
  )

  async function handleUpload() {
    if (files.length === 0) return
    setErrorMsg(null)

    try {
      const presignedResults = await Promise.all(
        files.map(async (file) => {
          const extension = (file.name.split('.').pop()?.toLowerCase() ?? 'bin').slice(0, 5)
          const res = await fetch(buildUrl('api', '/api/upload/presigned').toString(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fileName: file.name, contentType: file.type, bucket: 'content-private' }),
            credentials: 'include',
          })
          if (!res.ok) throw new Error(`Failed to get presigned URL for ${file.name}`)
          const { uploadUrl, key } = (await res.json()) as { uploadUrl: string; key: string }
          return { uploadUrl, key, extension, file, fileName: file.name, mimeType: file.type }
        }),
      )

      const uploadResults = await Promise.all(
        presignedResults.map(async ({ uploadUrl, file }) => {
          const res = await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: { 'Content-Type': file.type },
          }).catch(() => null)
          return res?.ok ?? false
        }),
      )

      if (!uploadResults.every(Boolean)) {
        toast.error('Gagal mengunggah beberapa file')
        return
      }

      addAttachmentMutation.mutate({
        learnProgressId,
        files: presignedResults.map((r) => ({
          key: r.key,
          fileName: r.fileName,
          extension: r.extension,
          mimeType: r.mimeType,
        })),
      })
    } catch {
      toast.error('Gagal mengunggah hasil kerja')
    }
  }

  return (
    <InClassUploadWorkModalBase
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) reset()
        onOpenChange(isOpen)
      }}
      files={files}
      onFilesChange={setFiles}
      onUpload={() => void handleUpload()}
      loading={addAttachmentMutation.isPending}
      errorMsg={errorMsg}
      childSubmissions={childSubmissions}
    />
  )
}
